import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useRenameBucket, s3ExtractError } from '@/api/s3';
import { validateBucketName } from './bucket-name-validation';

/**
 * Rename a bucket to a new (unused) name. Native rename is Wasabi-only; other
 * providers answer 501, which the shared error extractor surfaces verbatim as
 * a toast. Caller passes the current bucket name; `null` keeps the dialog shut.
 */
export function BucketRenameDialog({
  serviceId,
  bucket,
  onOpenChange,
}: {
  serviceId: string;
  bucket: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const rename = useRenameBucket(serviceId);
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);

  // Re-seed to the current name each time a different bucket opens the dialog.
  useEffect(() => {
    setName(bucket ?? '');
    setTouched(false);
  }, [bucket]);

  const unchanged = !!bucket && name.trim() === bucket;
  const error = touched && !unchanged ? validateBucketName(name.trim()) : null;

  const submit = () => {
    if (!bucket) return;
    setTouched(true);
    const next = name.trim();
    if (validateBucketName(next) || next === bucket) return;
    rename.mutate(
      { bucket, newName: next },
      {
        onSuccess: () => {
          toast.success('Bucket renamed');
          onOpenChange(false);
        },
        onError: (e) => toast.error(s3ExtractError(e, 'Rename not supported')),
      },
    );
  };

  return (
    <Dialog open={bucket !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename “{bucket}”</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="New name" required error={error ?? undefined}>
            {(p) => (
              <Input
                {...p}
                value={name}
                invalid={!!error}
                placeholder="e.g. my-media-assets"
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={rename.isPending || unchanged}
            onClick={submit}
          >
            {rename.isPending ? 'Renaming…' : 'Rename'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
